/**
 * TanStack Query MutationCache Configuration
 *
 * Centralizes toast notifications and error handling for all mutations.
 * Instead of each mutation hook manually calling toast.success/toast.error
 * in try/catch blocks, this MutationCache intercepts all mutation lifecycle
 * events and handles feedback automatically based on `meta` declarations.
 *
 * Usage in mutation hooks:
 * ```ts
 * useMutation({
 *   mutationFn: (data) => SomeService.update(id, data),
 *   meta: {
 *     successToast: 'Gespeichert',
 *     errorToast: 'Fehler beim Speichern',
 *   },
 * });
 * ```
 */

import { MutationCache, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { createLogger } from '@shared/lib';

const log = createLogger('MutationCache');

/** Sonner's default of 4 s is gone before the hand reaches the button. */
export const SUCCESS_ACTION_DURATION_MS = 8000;

// ─── Meta Type Declaration ─────────────────────────

export interface MutationMeta {
  /** Toast message on success. Set to `false` for silent mutations. */
  successToast?: string | false;
  /** Optional description below the success toast title. */
  successDescription?: string;
  /**
   * Optional button in the success toast — e.g. „Rückgängig". `onClick`
   * gets the mutation's result (typically the new id). The toast stays
   * longer so the button can actually be reached.
   */
  successAction?: { label: string; onClick: (data: unknown) => void };
  /** Toast message on error. Set to `false` to suppress error toast. */
  errorToast?: string | false;
  /** Custom error handler. Return `true` if handled (skips default toast). */
  onError?: (error: unknown) => boolean;
  /**
   * Marks an error as an expected outcome (e.g. the data changed meanwhile).
   * The default toast still shows, but it is logged as a warning instead of
   * an error — `log.error` persists to the `logs` collection.
   */
  expectedError?: (error: unknown) => boolean;
  /** Query keys to invalidate on settled (success or error). */
  invalidates?: readonly unknown[][];
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: MutationMeta;
  }
}

// ─── MutationCache Factory ─────────────────────────

/**
 * Create a MutationCache with centralized toast and error handling.
 *
 * Accepts a queryClient getter (thunk) for lazy access to avoid
 * circular initialization issues (cache is created before client).
 */
export function createMutationCache(getQueryClient: () => QueryClient): MutationCache {
  return new MutationCache({
    onSuccess: (data, _variables, _context, mutation) => {
      const meta = mutation.meta as MutationMeta | undefined;
      if (!meta || meta.successToast === false) return;

      if (meta.successToast) {
        const action = meta.successAction;
        toast.success(meta.successToast, {
          description: meta.successDescription,
          ...(action && {
            action: { label: action.label, onClick: () => action.onClick(data) },
            duration: SUCCESS_ACTION_DURATION_MS,
          }),
        });
      }
    },

    onError: (error, _variables, _context, mutation) => {
      const meta = mutation.meta as MutationMeta | undefined;

      // Let custom handler try first
      if (meta?.onError?.(error)) return;

      // Suppress toast if explicitly disabled
      if (meta?.errorToast === false) return;

      // Default error toast
      const title = meta?.errorToast || 'Ein Fehler ist aufgetreten';
      const description =
        error instanceof Error ? error.message : 'Ein unbekannter Fehler ist aufgetreten.';

      toast.error(title, { description });
      if (meta?.expectedError?.(error)) log.warn(title, error);
      else log.error(title, error);
    },

    onSettled: (_data, _error, _variables, _context, mutation) => {
      const meta = mutation.meta as MutationMeta | undefined;
      if (!meta?.invalidates?.length) return;

      const queryClient = getQueryClient();
      for (const queryKey of meta.invalidates) {
        queryClient.invalidateQueries({ queryKey: queryKey as unknown[] });
      }
    },
  });
}
